import { Link } from 'react-router-dom';
import { Shield, Zap, Users, Award, Target, Heart, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { COMPLIANCE_DISCLOSURE_TEXT, PORTAL_LINKS } from '../constants/site';

const TEAM = [
  { name: 'Andrew Sunna', role: 'Founder and Managing Director', photo: '/team/andrew-sunna.jpg' },
  { name: 'Ash', role: 'Finance Broker', photo: '/team/ash.jpg' },
  { name: 'Remon', role: 'Finance Broker', photo: '/team/remon.jpg' },
];

const About = () => {
  const values = [
    {
      icon: Zap,
      title: 'Speed',
      description: 'We understand time is money. Our streamlined processes mean faster approvals and quicker access to funds.'
    },
    {
      icon: Shield,
      title: 'Trust',
      description: 'Transparent dealings with no hidden fees. We build long-term relationships based on honesty and integrity.'
    },
    {
      icon: Heart,
      title: 'Service',
      description: 'Real people, real support. Our Australian team is here to guide you through every step of the process.'
    },
    {
      icon: Target,
      title: 'Results',
      description: 'We measure success by outcomes: clearer options, better lender fit, and faster funding decisions.'
    }
  ];

  const stats = [
    { value: '19', label: 'Years Industry Experience' },
    { value: '40+', label: 'Lenders on Panel' },
    { value: '24h', label: 'Approvals possible*' }
  ];

  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="About Us | 100% Australian Owned Finance Broker"
        description="Flash Funding is a 100% Australian owned broker helping Australians with car loans, personal loans, and business finance."
        keywords="Australian finance broker, commercial finance company, about Flash Funding, Australian owned business, Sydney finance broker"
        canonical="/about"
      />
      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1574662046936-d88d468acc95?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxzZW1pJTIwdHJ1Y2slMjBoaWdod2F5JTIwbW90aW9uJTIwYmx1cnxlbnwwfHx8fDE3NzA4NDUwODB8MA&ixlib=rb-4.1.0&q=85)'
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/90 to-navy-900/70"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/10 border border-gold-500/30 rounded-sm mb-8">
              <Shield size={16} className="text-gold-500" />
              <span className="text-gold-500 font-body text-sm font-medium">100% Australian Owned</span>
            </div>
            
            <h1 className="font-heading font-black text-5xl sm:text-6xl text-white uppercase tracking-tight leading-none mb-6">
              About<br />
              <span className="gold-gradient-text">Flash Funding</span>
            </h1>
            
            <p className="font-body text-lg sm:text-xl text-white/70 max-w-xl">
              We help Australians with Car Loans, Personal Loans, and Business Finance by matching each file to the right lender.
            </p>

            <p className="font-body text-sm text-white/60 max-w-xl mt-3" data-testid="about-hero-clarity-line">
              Instead of going direct to one lender, you get guided support and stronger option clarity from day one.
            </p>

            <div className="mt-7 flex flex-wrap gap-3" data-testid="about-hero-cta-row">
              <Link to="/car-loans" data-testid="about-hero-primary-link">
                <Button className="rounded-full bg-gradient-to-r from-purple-700 via-purple-600 to-purple-500 text-white font-heading font-bold uppercase tracking-[0.14em] text-xs px-7 py-6" data-testid="about-hero-primary-cta">
                  Check Your Options
                  <ArrowRight size={15} className="ml-2" />
                </Button>
              </Link>
              <Link to="/business-finance" data-testid="about-hero-secondary-link">
                <Button variant="outline" className="rounded-full border-purple-400/40 bg-navy-900/60 text-white hover:bg-purple-500/15 font-heading font-bold uppercase tracking-[0.14em] text-xs px-7 py-6" data-testid="about-hero-secondary-cta">
                  See What You Can Get Approved For
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 lg:py-28 bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading font-black text-4xl sm:text-5xl text-white uppercase tracking-tight mb-8">
                Our Story
              </h2>
              <div className="space-y-6 font-body text-white/70">
                <p>
                  Flash Funding was founded to make finance simpler for real people and real businesses.
                </p>
                <p>
                  We saw too many applicants pushed through slow, one-size-fits-all lender paths. So we built a model that focuses on lender fit first.
                </p>
                <p>
                  Today, we help with car loans, personal loans, and business finance while handling lender communication and process follow-through.
                </p>
                <p>
                  Based in Australia with local teams, we keep guidance practical, transparent, and focused on outcomes rather than jargon.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-3" data-testid="about-mid-cta-row">
                <Link to="/personal-loans" data-testid="about-mid-primary-link">
                  <Button className="rounded-full bg-purple-600 hover:bg-purple-500 text-white font-heading font-bold uppercase tracking-[0.14em] text-xs px-7 py-6" data-testid="about-mid-primary-cta">
                    Check Your Options
                  </Button>
                </Link>
                <a href={PORTAL_LINKS.GENERIC} data-testid="about-mid-secondary-link">
                  <Button variant="outline" className="rounded-full border-purple-400/40 bg-navy-900/60 text-white hover:bg-purple-500/15 font-heading font-bold uppercase tracking-[0.14em] text-xs px-7 py-6" data-testid="about-mid-secondary-cta">
                    See What You Can Get Approved For
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="/team/andrew-sunna.jpg"
                alt="Andrew Sunna, Founder and Managing Director of Flash Funding"
                className="rounded-lg shadow-2xl w-full max-w-md mx-auto aspect-square object-cover"
                data-testid="about-founder-photo"
              />
              <div className="absolute -bottom-6 -right-6 bg-gold-500 p-6 rounded-lg">
                <p className="font-heading font-bold text-3xl text-black">19</p>
                <p className="font-body text-sm text-black/70">Years industry experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-navy-900 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
            {stats.map((stat) => (
              <div key={stat.label} className="py-12 px-6 text-center">
                <p className="font-mono text-4xl sm:text-5xl font-bold text-gold-500 mb-2">{stat.value}</p>
                <p className="font-body text-white/60 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
          <p className="px-6 pb-6 text-center font-body text-xs text-white/50">*Approval timing depends on the applicant, the lender’s credit criteria and the type of loan.</p>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 lg:py-28 bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading font-black text-4xl sm:text-5xl text-white uppercase tracking-tight mb-4">
              Our Values
            </h2>
            <p className="font-body text-white/60 text-lg max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div 
                key={value.title}
                className="bg-navy-700 border border-white/5 p-8 text-center card-hover animate-fade-in"
                style={{ animationDelay: `${0.1 * index}s` }}
              >
                <div className="w-16 h-16 rounded-lg bg-gold-500/10 flex items-center justify-center mx-auto mb-6">
                  <value.icon size={32} className="text-gold-500" />
                </div>
                <h3 className="font-heading font-bold text-xl text-white uppercase tracking-tight mb-3">
                  {value.title}
                </h3>
                <p className="font-body text-white/60 text-sm">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 lg:py-28 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-navy-800 border border-white/5 p-6">
                  <Award size={32} className="text-gold-500 mb-4" />
                  <h4 className="font-heading font-bold text-lg text-white uppercase mb-2">Licensed</h4>
                  <p className="font-body text-white/60 text-sm">Authorised Credit Representative (ACR 578970) of Australian Credit Licence 414426</p>
                </div>
                <div className="bg-navy-800 border border-white/5 p-6">
                  <Users size={32} className="text-gold-500 mb-4" />
                  <h4 className="font-heading font-bold text-lg text-white uppercase mb-2">Expert Team</h4>
                  <p className="font-body text-white/60 text-sm">Led by our Founder and Managing Director, with 19 years of finance industry experience</p>
                </div>
                <div className="bg-navy-800 border border-white/5 p-6 col-span-2">
                  <Shield size={32} className="text-gold-500 mb-4" />
                  <h4 className="font-heading font-bold text-lg text-white uppercase mb-2">Australian Owned</h4>
                  <p className="font-body text-white/60 text-sm">Proudly 100% Australian owned and operated. Your money stays local, supporting Australian businesses and jobs.</p>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="font-heading font-black text-4xl sm:text-5xl text-white uppercase tracking-tight mb-8">
                Why Choose<br />
                <span className="gold-gradient-text">Flash Funding?</span>
              </h2>
              <div className="space-y-6 font-body text-white/70">
                <p>
                  In a market full of generic lending funnels, we keep advice specific and practical for your exact finance goal.
                </p>
                <p>
                  We work across personal and business finance and focus on lender fit, policy alignment, and speed to decision.
                </p>
                <p>
                  When you work with us, you get support from enquiry to outcome rather than being left to manage the process yourself.
                </p>
              </div>
              <Link to="/business-loans" className="inline-block mt-8" data-testid="about-final-cta-link">
                <Button 
                  data-testid="about-cta-btn"
                  className="gold-gradient text-black font-heading font-bold uppercase tracking-wider px-8 py-6 hover:scale-105 transition-transform gold-glow"
                >
                  Check Your Options
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-navy-800/70" data-testid="about-team-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-black text-4xl sm:text-5xl text-white uppercase tracking-tight mb-8" data-testid="about-team-heading">
            Meet the Team
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10" data-testid="about-team-grid">
            {TEAM.map((member) => (
              <article key={member.name} className="bg-navy-900 border border-white/10 overflow-hidden w-full max-w-[260px] mx-auto sm:max-w-none" data-testid={`about-team-card-${member.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <img src={member.photo} alt={`${member.name}, ${member.role} at Flash Funding`} loading="lazy" className="w-full aspect-square object-cover" />
                <div className="p-5">
                  <h3 className="text-white font-bold text-lg">{member.name}</h3>
                  <p className="text-white/70 text-sm">{member.role}</p>
                </div>
              </article>
            ))}
          </div>
          <p className="text-sm text-white/70" data-testid="about-compliance-disclosure-text">
            {COMPLIANCE_DISCLOSURE_TEXT}
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
